import { DEFAULT_FOLLOWUP_LABEL, isEmptyFollowUp, type FollowUp } from '@floorpoll/shared';
import type { RecapItem } from '../../lib/followUpCache.js';

/** The hostname alone, or null if the URL will not parse. */
function destinationHost(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

/**
 * The reply to one vote. The message is plain text on purpose — it is authored
 * by a stranger and rendered on someone else's phone, so it never goes near
 * dangerouslySetInnerHTML.
 */
export function FollowUpBlock({ followUp }: { followUp: FollowUp | null | undefined }) {
  if (!followUp || isEmptyFollowUp(followUp)) return null;
  const host = followUp.url ? destinationHost(followUp.url) : null;

  return (
    <div className="followup">
      {followUp.message ? <p className="followup-message">{followUp.message}</p> : null}
      {followUp.url && host ? (
        <>
          <a
            className="followup-link"
            href={followUp.url}
            // noreferrer, not habit: this page's URL is /j/<CODE>, and the
            // referrer header would hand the destination a live join code.
            target="_blank"
            rel="noopener noreferrer"
          >
            {followUp.linkLabel?.trim() || DEFAULT_FOLLOWUP_LABEL}
          </a>
          <span className="followup-host">opens {host}</span>
        </>
      ) : null}
    </div>
  );
}

/**
 * Everything this phone has collected, so advancing the screen takes nothing
 * away.
 *
 * The summary's content is a real h2, not styled text — a `<details>` whose
 * `<summary>` opens with a heading element is the one construct HTML permits
 * without breaking the outline (WHATWG "the summary element", content model).
 * That keeps the end-of-poll views at h1 (page title) -> h2 (this) -> h3
 * (each recap item) instead of jumping from h1 straight to h3, and lets this
 * h2 sit correctly beside the live view's per-question h2s.
 */
export function RecapList({ items }: { items: RecapItem[] }) {
  if (items.length === 0) return null;

  return (
    <details className="recap" open>
      <summary>
        <h2>Your picks ({items.length})</h2>
      </summary>
      {items.map((entry) => (
        <section className="recap-item" key={entry.questionId}>
          <h3>{entry.questionText}</h3>
          {/* One block per option picked. The choice repeats above each reply
              rather than being listed once, because a reply means nothing
              without the pick it answers. */}
          {entry.picks.map((pick, index) => (
            <div className="followup-pick" key={`${entry.questionId}-${index}`}>
              <p className="recap-choice">{pick.optionText}</p>
              <FollowUpBlock followUp={pick.followUp} />
            </div>
          ))}
        </section>
      ))}
    </details>
  );
}
